import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Alert,
} from "@mui/material";
import { styled } from "@mui/system";
import FeedbackService from "../../../services/feedback.service";

const FeedbackFormContainer = styled(Box)({
  marginTop: "12px",
  padding: "16px",
  backgroundColor: "#f8f9fa",
  borderRadius: "12px",
  border: "1px solid rgba(24, 116, 210, 0.12)",
});

const FeedbackForm = ({
  messageIndex,
  question,
  answer,
  contexts,
  feedbackType: initialFeedbackType,
  sessionId,
  userId,
  messageId,
  onClose,
  onSuccess,
}) => {
  const [feedbackType, setFeedbackType] = useState(initialFeedbackType || "neutral");
  const [comments, setComments] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [isLoadingFeedback, setIsLoadingFeedback] = useState(false);
  const [existingFeedback, setExistingFeedback] = useState(null);
  const [feedbackId, setFeedbackId] = useState(null);

  // Fetch existing feedback when component mounts or messageId changes
  useEffect(() => {
    const fetchExistingFeedback = async () => {
      // Reset form when messageId changes or is cleared
      setComments("");
      // Use initialFeedbackType if provided, otherwise default to "neutral"
      setFeedbackType(initialFeedbackType || "neutral");
      setExistingFeedback(null);
      setFeedbackId(null);
      setError(null);
      
      if (!messageId) {
        setIsLoadingFeedback(false);
        return;
      }

      setIsLoadingFeedback(true);

      try {
        const response = await FeedbackService.getFeedbackByMessageId(messageId);
        // Handle nested response structure: response.data.data
        const feedbackData = response?.data?.data || response?.data;
        if (feedbackData) {
          setExistingFeedback(feedbackData);
          
          // Populate form with existing feedback
          if (feedbackData.additional_comments) {
            setComments(feedbackData.additional_comments);
          }
          
          // Map API feedback_type to component state
          // Only use existing feedback if user hasn't made a new selection (initialFeedbackType is neutral or not provided)
          if (!initialFeedbackType || initialFeedbackType === "neutral") {
            if (feedbackData.feedback_type === "thumbs_up") {
              setFeedbackType("up");
            } else if (feedbackData.feedback_type === "thumbs_down") {
              setFeedbackType("down");
            } else {
              setFeedbackType("neutral");
            }
          } else {
            // User has made a new selection (clicked thumbs up/down), use that instead
            setFeedbackType(initialFeedbackType);
          }
          
          // Store feedback_id if available
          if (feedbackData.feedback_id) {
            setFeedbackId(feedbackData.feedback_id);
          }
        }
      } catch (err) {
        // If feedback doesn't exist, that's okay - just means it's a new feedback
        // Only log error if it's not a 404 or similar
        if (err.response?.status !== 404) {
          console.error("Error fetching existing feedback:", err);
        }
      } finally {
        setIsLoadingFeedback(false);
      }
    };

    fetchExistingFeedback();
  }, [messageId]);

  // Update feedbackType when initialFeedbackType changes (e.g., when user clicks thumbs up/down)
  // This should always update, even if existing feedback exists, to reflect user's current selection
  useEffect(() => {
    if (initialFeedbackType && initialFeedbackType !== "neutral") {
      setFeedbackType(initialFeedbackType);
    }
  }, [initialFeedbackType]);

  const handleSubmit = async () => {
    // Allow submission if comments are provided
    if (isSubmitting || !comments.trim()) return;

    setIsSubmitting(true);
    setError(null);

    try {
      // Hardcoded context structure
      const hardcodedContexts = [
        {
          source: "string",
          text: "string",
          relevance_score: 0
        }
      ];

      // Map feedback type to API format
      const finalFeedbackType = feedbackType === "neutral" 
        ? "neutral"
        : feedbackType === "up" 
        ? "thumbs_up" 
        : "thumbs_down";

      const payload = {
        question: String(question || ""),
        answer: String(answer || ""),
        contexts: hardcodedContexts,
        feedback_type: finalFeedbackType,
        additional_comments: String(comments.trim() || ""),
        user_id: String(userId || "anonymous"),
        session_id: String(sessionId || ""),
      };

      const response = await FeedbackService.submitFeedback(payload);
      
      // Extract message_id and feedback_id from response
      // Handle nested response structure: response.data.data or response.data
      const responseData = response?.data?.data || response?.data;
      let responseMessageId = messageId;
      let responseFeedbackId = feedbackId;
      
      if (responseData) {
        if (responseData.message_id) {
          responseMessageId = responseData.message_id;
        }
        if (responseData.feedback_id) {
          responseFeedbackId = responseData.feedback_id;
          setFeedbackId(responseFeedbackId);
        }
      }
      
      setSuccess(true);
      if (onSuccess) {
        // Pass message_id and feedback_id back to parent
        onSuccess({
          message_id: responseMessageId,
          feedback_id: responseFeedbackId,
        });
      }
      
      // Auto close after 2 seconds
      setTimeout(() => {
        if (onClose) {
          onClose();
        }
      }, 2000);
    } catch (err) {
      console.error("Feedback submission error:", err);
      
      // Handle different error response formats
      let errorMessage = "Failed to submit feedback. Please try again.";
      
      if (err.response?.data) {
        const errorData = err.response.data;
        
        // Handle Pydantic validation errors (array of error objects)
        if (Array.isArray(errorData.detail)) {
          errorMessage = errorData.detail
            .map((e) => {
              if (typeof e === "string") return e;
              if (typeof e === "object" && e.msg) {
                const loc = Array.isArray(e.loc) ? e.loc.join(".") : "";
                return `${loc ? `${loc}: ` : ""}${e.msg}`;
              }
              return JSON.stringify(e);
            })
            .join(", ");
        } 
        // Handle string error message
        else if (typeof errorData.detail === "string") {
          errorMessage = errorData.detail;
        }
        // Handle object error
        else if (typeof errorData.detail === "object") {
          errorMessage = errorData.detail.msg || JSON.stringify(errorData.detail);
        }
        // Handle other error formats
        else if (errorData.message) {
          errorMessage = errorData.message;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (success) {
    return (
      <FeedbackFormContainer>
        <Alert severity="success" sx={{ mb: 1 }}>
          Thank you for your feedback!
        </Alert>
      </FeedbackFormContainer>
    );
  }

  if (isLoadingFeedback) {
    return (
      <FeedbackFormContainer>
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 2 }}>
          <CircularProgress size={20} />
          <Typography variant="body2" sx={{ ml: 1, color: "#666666" }}>
            Loading feedback...
          </Typography>
        </Box>
      </FeedbackFormContainer>
    );
  }

  return (
    <FeedbackFormContainer>
      <Typography
        variant="body2"
        sx={{
          color: "#002855",
          fontWeight: 500,
          fontSize: "0.875rem",
          mb: 1.5,
        }}
      >
        {existingFeedback ? "Update your feedback" : "Share your feedback"}
      </Typography>
      
      <TextField
        fullWidth
        multiline
        rows={3}
        placeholder="Share your thoughts (optional)..."
        value={comments}
        onChange={(e) => setComments(e.target.value)}
        variant="outlined"
        size="small"
        sx={{
          mb: 1.5,
          "& .MuiOutlinedInput-root": {
            backgroundColor: "#ffffff",
            fontSize: "0.875rem",
            "& fieldset": {
              borderColor: "rgba(24, 116, 210, 0.2)",
            },
            "&:hover fieldset": {
              borderColor: "rgba(24, 116, 210, 0.4)",
            },
            "&.Mui-focused fieldset": {
              borderColor: "rgba(24, 116, 210, 0.6)",
            },
          },
        }}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 1.5 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
        <Button
          size="small"
          onClick={onClose}
          disabled={isSubmitting}
          sx={{
            textTransform: "none",
            color: "#666666",
            "&:hover": {
              backgroundColor: "rgba(0, 0, 0, 0.04)",
            },
          }}
        >
          Cancel
        </Button>
        <Button
          size="small"
          variant="contained"
          onClick={handleSubmit}
          disabled={isSubmitting || !comments.trim()}
          sx={{
            textTransform: "none",
            backgroundColor: "rgba(24, 116, 210, 1)",
            "&:hover": {
              backgroundColor: "rgba(24, 116, 210, 0.9)",
            },
            "&:disabled": {
              backgroundColor: "rgba(0, 0, 0, 0.12)",
            },
            minWidth: "100px",
          }}
        >
          {isSubmitting ? (
            <CircularProgress size={16} sx={{ color: "#ffffff" }} />
          ) : (
            existingFeedback ? "Update" : "Submit"
          )}
        </Button>
      </Box>
    </FeedbackFormContainer>
  );
};

export default FeedbackForm;

